import React, { useEffect, useRef, useState } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import styled from '@emotion/styled';
import { useContextSelector } from 'use-context-selector';

import { moveNodeCommand } from '../../commands';
import { EditorContext } from '../../contexts';
import { COLORS, hex2rgba } from '../../styles';
import { DocumentPath } from '../../types';
import { canMoveNode } from '../../utils';

const TREE_ITEM = 'TREE_ITEM';
interface TreeItem {
  path: DocumentPath;
}

interface Props {
  children: React.ReactNode;
  path: DocumentPath;
  disabled?: boolean;
}

type DROP_POINT = 'top' | 'bottomLeft' | 'bottomRight';

export default function Draggable({ path, children, disabled }: Props) {
  const source = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    {
      type: TREE_ITEM,
      item: (): TreeItem => ({ path }),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    },
    [path],
  );

  const [{ isOver }, drop] = useDrop(
    {
      accept: TREE_ITEM,
      hover: handleHover,
      drop: handleDrop,
      collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
    },
    [path],
  );

  drag(drop(source));

  const dropPointRef = useRef<DROP_POINT | null>(null);
  const [dropPoint, setDropPoint] = useState<DROP_POINT | null>(null);
  function handleHover(item: TreeItem, monitor: DropTargetMonitor) {
    if (!source.current) { return; }
    if (!monitor.getClientOffset()) { return; }
    const clientOffset = monitor.getClientOffset()!;
    const sourceRects = source.current.getBoundingClientRect();
    const sourceMiddleX = sourceRects.x + sourceRects.width / 2;
    const sourceMiddleY = sourceRects.y + sourceRects.height / 2;

    if (clientOffset.y < sourceMiddleY) {
      dropPointRef.current = 'top';
      setDropPoint('top');
    } else if (clientOffset.x < sourceMiddleX) {
      dropPointRef.current = 'bottomLeft';
      setDropPoint('bottomLeft');
    } else {
      dropPointRef.current = 'bottomRight';
      setDropPoint('bottomRight');
    }
  }

  const { components, getState, dispatch } = useContextSelector(
    EditorContext,
    (context) => context!,
  );
  function handleDrop(item: TreeItem, monitor: DropTargetMonitor) {
    if (monitor.didDrop()) { return; }
    const destPath: DocumentPath =
      (dropPointRef.current === 'top' && [...path]) ||
      (dropPointRef.current === 'bottomLeft' && [
        ...path.slice(0, -1),
        path[path.length - 1] + 1,
      ]) ||
      (dropPointRef.current === 'bottomRight' && [...path, 0]) ||
      item.path;

    if (canMoveNode(components, getState().document, item.path, destPath)) {
      dispatch(moveNodeCommand(item.path, destPath));
    }
  }

  useEffect(() => {
    if (isOver) { return; }
    setDropPoint(null);
  }, [isOver]);

  // for root node
  if (path.length === 0) {
    return <>{ children }</>;
  }

  if (disabled) {
    return <>{ children }</>;
  }

  return (
    <S.Container ref={source} dimmed={isDragging}>
      { children }
      { isOver && dropPoint && <S.DropPointer position={dropPoint} /> }
    </S.Container>
  );
}

const S = {
  Container: styled.div<{ dimmed: boolean }>`
    position: relative;
    ${({ dimmed }) => dimmed &&
      `
      pointer-events: none;
      opacity: .4;
    `}
  `,
  DropPointer: styled.div<{ position: DROP_POINT }>`
    position: absolute;
    height: 2px;
    background: ${COLORS.blue50};
    transform: translateY(-50%);
    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 5px;
      height: 5px;
      border-radius: 10px;
      background: ${COLORS.blue50};
      transform: translate3d(-50%, -50%, 0);
    }
    ${({ position }) => (position === 'top' &&
        `
      top: 0;
      left: 0;
      width: 100%;
    `) ||
      (position === 'bottomLeft' &&
        `
      top: 100%;
      left: 0;
      width: 100%;
    `) ||
      (position === 'bottomRight' &&
        `
      top: 100%;
      left: 20px;
      right: 0;
    `)}
  `,
};
